import { memo, useEffect, useMemo } from 'react';

import {
  useIsAllowedPermission,
  usePermissions,
} from '@mint-lib/profile-helpers';
import styled, { css } from '@mint-lib/styled';
import type {} from '@mint-lib/theme/src/extensions/chip.js';
import { equals } from 'remeda';

import { Box, Divider, Skeleton } from '@myn/mui';

import {
  useGetBudgetRows,
  useSetPredictiveBudget,
} from '../../../hooks/useBudgetRows.js';
import useCheckHistoricalData from '../../../hooks/useCheckHistoricalData.js';
import { useBuyingPlanInfoSelector } from '../../../hooks/useGetBuyingPlanData.js';
import { useGetCampaignData } from '../../../hooks/useGetCampaignData.js';
import { useGetMediaPlanRows } from '../../../hooks/useGetMediaPlanRows.js';
import { resetPredictivePlanningQueryData } from '../../../hooks/useGetPredictivePlanningData.js';
import { useIsArchivedVersion } from '../../../hooks/useIsArchivedVersion.js';
import useIsBuyingPlanLocked from '../../../hooks/useIsBuyingPlanLocked.js';
import { useSetSelectedKPI } from '../../../hooks/usePredictivePlanningMetrics.js';
import {
  usePredictivePlanningMode,
  useSetPredictivePlanningMode,
} from '../../../hooks/usePredictivePlanningMode.js';
import { prepareApprovalStatusBarProps } from '../../../utils/approvalFlow.js';
import { formatVersionName } from '../../../utils/formatters.js';
import { PanelTab } from '../PanelTab.js';
import BudgetList from './BudgetList.jsx';
import Archived from './BuilderHeaderBlocks/Archived.jsx';
import DividerBlock from './BuilderHeaderBlocks/DividerBlock.jsx';
import Header from './BuilderHeaderBlocks/Header.jsx';
import DateField from './DateField.jsx';
import MediaPlanName from './MediaPlanName.jsx';
import MediaPlanSelect from './MediaPlanSelect.jsx';
import PredictiveSettings from './PredictiveSettings.jsx';
import TableSettings from './TableSettings.jsx';
import useViewportWidth from './useViewportWidth.jsx';
import { getBudgetRows } from './utils.js';

export type TabStateType = {
  currentTab: PanelTab | null;
  setCurrentTab: (tab: PanelTab | null) => void;
};

export type Props = {
  tabState: TabStateType;
  isLoading?: boolean;
};

const BuilderHeader: React.FC<Props> = memo(({ tabState, isLoading }) => {
  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const [isArchivedVersion] = useIsArchivedVersion();
  const { getPermissionField } = usePermissions();

  const selectedPlan = useBuyingPlanInfoSelector();
  const [currentBudgetRows, setBudgetRows] = useGetBudgetRows();
  const isPredictivePlanning = usePredictivePlanningMode();
  const setPredictivePlanningMode = useSetPredictivePlanningMode();
  const setSelectedKPI = useSetSelectedKPI();
  const { isLocked: isBuyingPlanLocked } = useIsBuyingPlanLocked();
  const { data: campaign } = useGetCampaignData();
  const preparedRows = useGetMediaPlanRows();

  const { status, isLoading: isHistoricalDataLoading } =
    useCheckHistoricalData();

  const isPredictiveLoading = isHistoricalDataLoading && preparedRows.length;

  const { isDesktopWidth } = useViewportWidth();
  const setPredictiveBudget = useSetPredictiveBudget();

  const isNonFinancialUser = !getPermissionField('financialDataFeature');

  const isRenameMediaPlanButtonDisabled =
    !selectedPlan ||
    !isAllowed ||
    isPredictivePlanning ||
    isArchivedVersion ||
    isBuyingPlanLocked;
  const isChangeMediaPlanButtonDisabled = !selectedPlan || isPredictivePlanning;

  const versionName = formatVersionName(selectedPlan?.version ?? '');

  const isAllRowsLockedForPredictive = useMemo(() => {
    if (!currentBudgetRows) {
      return false;
    }

    return Object.values(currentBudgetRows).every((row) => row.isLocked);
  }, [currentBudgetRows]);

  const isDataForApprovalFlow = campaign && selectedPlan;
  const innerObj =
    isDataForApprovalFlow &&
    prepareApprovalStatusBarProps({
      campaign,
      selectedPlan,
    });

  const handleOpenVersioning = () => {
    tabState.setCurrentTab(PanelTab.Versions);
  };

  const handleOpenPredictivePlanning = () => {
    if (selectedPlan?.channelMixBudget) {
      setPredictiveBudget(Number(selectedPlan.channelMixBudget));
    }
    setPredictivePlanningMode(true);
    setSelectedKPI(null);
    resetPredictivePlanningQueryData();
  };

  useEffect(() => {
    if (isPredictivePlanning || !selectedPlan) {
      return;
    }

    const newBudgetRows = getBudgetRows({
      rows: preparedRows,
      planStartDate: selectedPlan.dateFrom,
      planEndDate: selectedPlan.dateTo,
    });

    if (!equals(newBudgetRows, currentBudgetRows ?? {})) {
      setBudgetRows(newBudgetRows);
    }
  }, [preparedRows, currentBudgetRows, selectedPlan]);

  useEffect(() => {
    return () => {
      setPredictivePlanningMode(false);
    };
  }, []);

  return (
    <>
      <Header
        innerObj={innerObj}
        isPlanReadyForReview={selectedPlan?.isReadyForReview ?? false}
      />
      <SettingsContainer>
        <SettingsHeader $isDesktop={isDesktopWidth}>
          <ControlsContainer>
            {isLoading ? (
              <Box
                height={36}
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Skeleton variant="rounded" width={140} />
                <Skeleton variant="rounded" width={80} />
              </Box>
            ) : (
              <>
                <ControlsLeft>
                  <MediaPlanName
                    selectedPlan={selectedPlan ?? null}
                    editingDisabled={isRenameMediaPlanButtonDisabled}
                  />
                  <MediaPlanSelect
                    selectedPlanId={selectedPlan?.id ?? null}
                    editingDisabled={isChangeMediaPlanButtonDisabled}
                  />
                </ControlsLeft>

                <Archived
                  isArchivedVersion={isArchivedVersion}
                  versionName={versionName}
                  isDisabled={isBuyingPlanLocked}
                  handleOpenVersioning={handleOpenVersioning}
                />
              </>
            )}
          </ControlsContainer>
          <DividerBlock isDesktopWidth={isDesktopWidth} />
          <ColumnsContainer $isDesktop={isDesktopWidth}>
            {isLoading ? (
              <Skeleton variant="rounded" width={170} />
            ) : (
              <DateField
                dateFrom={selectedPlan?.dateFrom ?? null}
                dateTo={selectedPlan?.dateTo ?? null}
                variant={isDesktopWidth ? 'default' : 'chip'}
              />
            )}
            {isLoading && !isNonFinancialUser ? (
              <>
                <Skeleton width={120} variant="rounded" />
                <Skeleton width={85} variant="rounded" />
                <Skeleton width={120} variant="rounded" />
              </>
            ) : null}

            {!isNonFinancialUser && !isLoading ? (
              <BudgetList
                selectedPlan={selectedPlan ?? null}
                currency={campaign?.currency.value ?? null}
                variant={isDesktopWidth ? 'default' : 'chip'}
              />
            ) : null}
          </ColumnsContainer>
        </SettingsHeader>
        <StyledDivider />
        {isPredictivePlanning && selectedPlan ? (
          <PredictiveSettings
            selectedPlan={selectedPlan}
            currency={campaign?.currency}
            isAllRowsLocked={isAllRowsLockedForPredictive}
          />
        ) : (
          <TableSettings
            selectedPlanId={selectedPlan?.id ?? null}
            climatePartnerError={selectedPlan?.climatePartnerError ?? null}
            isPredictiveLoading={isPredictiveLoading}
            haveMediaRows={Boolean(preparedRows.length)}
            status={status}
            openPredictiveMode={handleOpenPredictivePlanning}
          />
        )}
      </SettingsContainer>
    </>
  );
});

const SettingsContainer = styled(Box)`
  padding: ${({ theme }) => theme.spacing(1.5)};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.palette.ui.primary};
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.15),
    0px 0px 5px 0px rgba(23, 24, 24, 0.05);
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing(2)};
  }
`;

const SettingsHeader = styled(Box)<{ $isDesktop: boolean }>`
  display: flex;

  ${({ $isDesktop, theme }) =>
    $isDesktop
      ? css`
          align-items: center;
          column-gap: ${theme.spacing(3)};
          justify-content: space-between;
        `
      : css`
          flex-direction: column;
          align-items: left;
        `}
`;

const ControlsContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: ${({ theme }) => theme.spacing(3)};
  flex: 1 1 auto;
`;

const ControlsLeft = styled(Box)`
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(1)};
  flex: 1 1 auto;
`;

const ColumnsContainer = styled(Box)<{ $isDesktop: boolean }>`
  ${({ $isDesktop, theme }) => css`
    display: flex;
    align-items: center;
    row-gap: ${theme.spacing(1)};
    column-gap: ${$isDesktop ? theme.spacing(3) : theme.spacing(1)};
    flex-wrap: ${$isDesktop ? 'nowrap' : 'wrap'};
  `}
`;

const StyledDivider = styled(Divider)`
  width: 100%;
  margin: ${({ theme }) => `${theme.spacing(1.5)} 0px`};
`;

export default BuilderHeader;
